import { LitElement, html } from 'lit';

import { formatNumber, formatPercent } from '../../utils/intl';
import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import translate from '../../utils/webcomponents/translate';
import constructDialog from '../../utils/webcomponents/construct-dialog';

import alertEvents from '../alert/alert-events';
import * as portfolioUtils from './portfolio-utils';
import portfolioEvents from './portfolio-events';
import * as portfolioApi from './portfolio-api';
import watchlistEvents from '../watchlist/watchlist-events';
import * as watchlistApi from '../watchlist/watchlist-api';

import '../message';
import './portfolio-performance';
import './portfolio-table';
import './portfolio-item-edit-form';
import './portfolio-item-remove-form';

const CURRENCY_DEFAULT = 'EUR';

class Portfolio extends LitElement {
	static get properties() {
		return {
			name: { type: String },
			currency: { type: String },
			hasLoaded: { type: Boolean },
			isLoading: { type: Boolean },
			isError: { type: Boolean },
			isWatchlistActive: { type: Boolean },
			items: { type: Array },
			showIsinColumn: { type: Boolean },
			hideCurrencyColumn: { type: Boolean },
		};
	}

	constructor() {
		super();

		this.items = [];
		this.isWatchlistActive = false;

		this.showIsinColumn = false;
		this.hideCurrencyColumn = false;

		this.selected = { id: null, name: 'Default' };

		this.hasLoaded = false;
		this.isLoading = false;
		this.isError = false;
	}

	get portfolioCurrency() {
		return this.currency || CURRENCY_DEFAULT;
	}

	get activeItems() {
		return this.items.filter((i) => !i.isProductInactive);
	}

	get containsForeignCurrencies() {
		return this.activeItems.some((i) => i.currency !== this.portfolioCurrency);
	}

	get showCurrencyColumn() {
		return !this.hideCurrencyColumn;
	}

	async load() {
		this.isLoading = true;
		this.isError = false;

		try {
			const allPortfolios = await portfolioApi.getAll();
			const [{ portfolioId: id, name } = {}] = allPortfolios;

			if (!id) {
				throw Error('Could not fetch the default portfolio');
			}

			this.selected = { id, name };
		} catch (e) {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}

		this.updateCurrentItems();
	}

	async updateCurrentItems() {
		this.isLoading = true;
		this.isError = false;

		try {
			const {
				portfolioItems = [],
				showTransactionButton,
				showIsinColumn,
				hideCurrencyColumn,
			} = await portfolioApi.get(this.selected.id);

			this.items = portfolioItems
				.sort((a, b) => {
					const aInactive = a.isProductInactive;
					const bInactive = b.isProductInactive;

					return Number(aInactive) - Number(bInactive);
				})
				.map((item) => ({
					...item,
					showTransactionButton,
				}));

			this.showIsinColumn = showIsinColumn;
			this.hideCurrencyColumn = hideCurrencyColumn;

			const isEmpty = this.items.length === 0;

			this.emptyMessageContainer.classList.toggle('is-hidden', !isEmpty);
		} catch (e) {
			console.warn(e);

			this.isError = true;
		} finally {
			this.isLoading = false;
			this.hasLoaded = true;
		}
	}

	render() {
		if (this.isError) {
			return this.renderError();
		}

		return this.renderPortfolio();
	}

	renderPortfolio() {
		const loader = this.renderLoader();
		const header = this.renderHeader();
		const table = this.renderProductTable();

		return html`${loader} ${header} ${table}`;
	}

	renderHeader() {
		if (!this.hasLoaded && this.isLoading) {
			return html``;
		}

		return this.containsForeignCurrencies ? this.renderCurrenciesError() : this.renderPerformance();
	}

	renderPerformance() {
		return html`
			<x-portfolio-performance
				class="portfolio-performance"
				.items="${this.activeItems}"
				.currency="${this.portfolioCurrency}"
			></x-portfolio-performance>
		`;
	}

	renderCurrenciesError() {
		const message = translate(
			'portfolio',
			'portfolioContainingForeignCurrencies',
			'Your portfolio holds foreign currencies',
		);

		return html`
			<x-message
				message="${message}"
				icon="warning"
				iconSize="m"
				modifier="warning"
			></x-message>
		`;
	}

	renderProductTable() {
		return html`
			<x-portfolio-table
				.items="${this.items}"
				.isWatchlistActive="${this.isWatchlistActive}"
				.showIsinColumn="${this.showIsinColumn}"
				.showCurrencyColumn="${this.showCurrencyColumn}"
			>
			</x-portfolio-table>
		`;
	}

	renderLoader() {
		return this.isLoading ? html`<x-loader .isLoading="${this.isLoading}"></x-loader>` : html``;
	}

	renderError() {
		const errorFeedback = translate('portfolio', 'somethingWentWrong', 'Something went wrong. Please try again.');
		const labelRetry = translate('portfolio', 'retry', 'Retry');

		return html`
			<div class="trailer">
				<x-message
					modifier="error"
					icon="warning"
					message="${errorFeedback}"
				></x-message>
				<button
					class="button"
					@click="${this.load}"
				>
					${labelRetry}
				</button>
			</div>
		`;
	}

	connectedCallback() {
		super.connectedCallback();

		this.emptyMessageContainer = document.body.querySelector('.js-empty-message');

		// event getting dispatched from body due to a IE11 bug
		document.body.addEventListener(portfolioEvents.PORTFOLIO_CHANGED, () => this.onPortfolioChanged());
		document.body.addEventListener(alertEvents.ERROR, (data) => this.onItemStoplossAlertError(data));
		document.body.addEventListener(portfolioEvents.ITEM_REMOVE_SUCCESS, (e) => this.onItemRemovedSuccess(e));
		document.body.addEventListener(watchlistEvents.ITEM_TOGGLE, (e) => this.onToggleWatchlist(e));

		this.addEventListener(portfolioEvents.ITEM_REMOVE, this.onItemRemove);
		this.addEventListener(portfolioEvents.ITEM_EDIT, this.onItemEdit);
		this.addEventListener(portfolioEvents.ITEM_STOPLOSS_ALERT, this.onItemStoplossAlertEdit);
		this.addEventListener(portfolioEvents.ITEM_TICK, this.onItemTick);
		this.addEventListener(portfolioEvents.ITEM_MOVE, this.onItemMove);

		this.load();
	}

	onPortfolioChanged() {
		this.updateCurrentItems();
	}

	onItemRemove(e) {
		const { detail: item } = e;

		this.createDialog('x-portfolio-item-remove-form', item);
	}

	onItemRemovedSuccess({ detail: removedItem = {} }) {
		const existingItems = this.items.filter((i) => i.id !== removedItem.id);

		this.items = [...existingItems];
	}

	onItemMove() {
		const portfolioItemIds = Array.from(this.querySelectorAll('x-portfolio-item')).map((item) => item.id);

		portfolioApi.updateOrder(this.selected.id, JSON.stringify({ portfolioItemIds }));

		this.requestUpdate();
	}

	onItemEdit(e) {
		const { detail: item } = e;

		this.createDialog('x-portfolio-item-edit-form', item);
	}

	onItemStoplossAlertEdit({ detail: portfolioItem }) {
		this.updateStopLossAlerts(portfolioItem);
	}

	onItemStoplossAlertError({ detail: { isActivated, isin } }) {
		this.syncItemStates(isin, !isActivated, 'hasStopLossLevelReachedNotification');
	}

	updateStopLossAlerts({ isin, hasStopLossLevelReachedNotification: isActivated }) {
		const update = {
			isActivated,
			isin,
			type: 1,
		};

		// update all toggles directly
		this.syncItemStates(isin, isActivated, 'hasStopLossLevelReachedNotification');

		dispatchCustomEvent(this, alertEvents.UPDATE, update);
	}

	sortItems(items = []) {
		const inactiveItems = items.filter((item) => item.isProductInactive);
		const activeItems = items.filter((item) => !item.isProductInactive);

		return [...activeItems, ...inactiveItems];
	}

	syncItemStates(isin, value, property) {
		this.items = this.items.map((item) => {
			if (item.isin === isin) {
				item = {
					...item,
					[property]: value,
				};
			}

			return item;
		});

		this.requestUpdate();
	}

	onItemTick(e) {
		const item = this.getItemFromEvent(e);
		const { tick } = e.detail;

		if (!item || !tick) {
			console.warn('no item or tick', { item, tick });

			return;
		}

		const { Bid: bid } = tick;

		item.currentValue = portfolioUtils.itemValue(bid, item.amount);
		item.currentValueFormatted = formatNumber(item.currentValue);

		item.profit = portfolioUtils.itemProfit(item.currentValue, item.investedValue);
		item.profitFormatted = formatPercent(item.profit);

		item.profitMonetary = portfolioUtils.itemProfitMonetary(item.currentValue, item.investedValue);
		item.profitMonetaryFormatted = formatNumber(item.profitMonetary);

		// reassign to trigger update
		this.items = [...this.items];
	}

	onToggleWatchlist({ detail: { isin, isInWatchlist } }) {
		if (isInWatchlist) {
			this.addToWatchlist(isin, isInWatchlist);
		} else {
			this.removeFromWatchlist(isin, isInWatchlist);
		}

		// update all items directly
		this.syncItemStates(isin, isInWatchlist, 'isInWatchlist');
	}

	async addToWatchlist(isin, isInWatchlist) {
		try {
			await watchlistApi.add(isin);
		} catch (e) {
			this.syncItemStates(isin, !isInWatchlist, 'isInWatchlist');
		}
	}

	async removeFromWatchlist(isin, isInWatchlist) {
		try {
			await watchlistApi.remove(isin);
		} catch (e) {
			this.syncItemStates(isin, !isInWatchlist, 'isInWatchlist');
		}
	}

	getItemFromEvent(event) {
		const { detail: { id } = {} } = event;

		if (!id) {
			return false;
		}

		return this.items.find((i) => i.id === id);
	}

	createDialog(childTag, portfolioItem) {
		const portfolioId = this.selected.id;
		const options = { ...portfolioItem, portfolioId };
		const { productName: title } = options;

		return constructDialog({ title }, childTag, { options: JSON.stringify(options) });
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio', Portfolio);

export default Portfolio;
